import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import Icon from '../Icon';
import Typography from '../Typography';

const borderStyle = 'border-b last:border-b-0 border-secondary-main';

const DropdownFilter = ({
  id,
  children,
  showDropdownIcon,
  list,
  itemsClassName,
  titleClassName,
  showBorders,
  alignment,
  maxCharactersPerLine,
  widthClassName = 'w-[220px]',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [filteredList, setFilteredList] = useState([...list]);
  const element = useRef(null);
  const inputRef = useRef(null);

  // choose the max characters per line based on the longest title
  const longestTitle = list.reduce((acc, item) => {
    if (item.title.length > acc) {
      return item.title.length;
    }
    return acc;
  }, 0);

  maxCharactersPerLine = maxCharactersPerLine ?? longestTitle;

  const DropdownItem = useCallback(
    ({ id, title, titleIcon, icon, onClick, onClickIcon }) => {
      // Split the title into lines of length maxCharactersPerLine
      const lines = [];
      for (let i = 0; i < title.length; i += maxCharactersPerLine) {
        lines.push(title.substring(i, i + maxCharactersPerLine));
      }

      return (
        <div
          key={title}
          className={classnames(
            'hover:bg-secondary-main flex cursor-pointer items-center px-4 py-2 transition duration-300 ',
            titleClassName,
            showBorders && borderStyle
          )}
          onClick={() => {
            setOpen(false);
            onClick();
          }}
          data-cy={id}
        >
          {!!icon && (
            <span title={titleIcon}>
              <Icon
                name={icon}
                className={itemsClassName + ' mr-2 w-4'}
                onClick={onClickIcon}
              />
            </span>
          )}
          <div
            style={{
              whiteSpace: 'nowrap',
            }}
            className="truncate flex-1"
          >
            {title.length > maxCharactersPerLine && (
              <div className="truncate">
                {lines.map((line, index) => (
                  <Typography
                    key={index}
                    className={classnames('truncate', itemsClassName)}
                  >
                    {line}
                  </Typography>
                ))}
              </div>
            )}
            {title.length <= maxCharactersPerLine && (
              <Typography className={classnames('truncate', itemsClassName)}>{title}</Typography>
            )}
          </div>
        </div>
      );
    },
    [maxCharactersPerLine, itemsClassName, titleClassName, showBorders]
  );

  const renderTitleElement = () => {
    return (
      <div className="flex items-center">
        {children}
        {showDropdownIcon && (
          <Icon
            name="chevron-down"
            className="ml-1"
          />
        )}
      </div>
    );
  };

  const toggleList = () => {
    setOpen(s => !s);
  };

  const handleClick = e => {
    if (element.current && !element.current.contains(e.target)) {
      setOpen(false);
    }
  };

  const renderList = () => {
    return (
      <div
        className={classnames(
          'top-100 border-secondary-main absolute z-10 mt-2 transform rounded border bg-black shadow transition duration-300',
          widthClassName,
          {
            'right-0 origin-top-right': alignment === 'right',
            'left-0 origin-top-left': alignment === 'left',
            'scale-0': !open,
            'scale-100': open,
          }
        )}
        data-cy={`${id}-dropdown`}
      >
        <>
          <input
            ref={inputRef}
            type="text"
            className="w-full px-4 py-2 text-sm bg-secondary-main text-white placeholder-gray-400 focus:outline-none"
            placeholder={t('Common:Search')}
            onChange={e => {
              const searchTerm = e.target.value.toLowerCase();
              const filtered = list.filter(item =>
                item.title.toLowerCase().includes(searchTerm)
              );
              setFilteredList(filtered);
            }}
          />

          <div className="ohif-scrollbar overflow-auto max-h-96">
            {filteredList.map((item, idx) => (
              <DropdownItem
                id={item.id}
                title={item.title}
                titleIcon={item.titleIcon}
                icon={item.icon}
                onClick={item.onClick}
                onClickIcon={item.onClickIcon}
                key={idx}
              />
            ))}
          </div>

          {filteredList.length === 0 && (
            <div className="px-4 py-2 text-gray-400 w-[150px]">
              {t('Header:No results found')}
            </div>
          )}
        </>
      </div>
    );
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);

    if (!open) {
      document.removeEventListener('click', handleClick);
    }
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    setFilteredList(list);
  }, [list]);

  return (
    <div
      data-cy="dropdown"
      ref={element}
      className="relative"
    >
      <div
        className="flex cursor-pointer items-center"
        onClick={toggleList}
      >
        {renderTitleElement()}
      </div>

      {renderList()}
    </div>
  );
};


export default DropdownFilter;
